import React, { useContext, useState, useMemo } from "react";
import { PerformerContext } from "../contexts/PerformerContext";
import { PerformerAWContext } from "../contexts/PerformerAWContext";
import { Box, Typography, InputBase, Divider } from "@mui/material";
import { Link } from "react-router-dom";
import SearchIcon from "@mui/icons-material/Search";

// ── Category definitions — thumbnails picked from loaded performers ───────────
const CATEGORY_DEFS = [
  { label: "Latina",       emoji: "🌶️", to: "/category/latina-cam-girls",         match: p => p.ethnicity?.toLowerCase() === 'hispanic' },
  { label: "MILF",         emoji: "🔥", to: "/category/milf-cam-girls",            match: p => { const age = parseInt(p.age); return age >= 30 && age <= 55 && (/milf|housewif/i.test(p.theme || '') || (p.fetishes || []).some(f => /milf|housewif/i.test(f))); } },
  { label: "Teen 18+",     emoji: "✨", to: "/category/teen-cam-girls",            match: p => parseInt(p.age) >= 18 && parseInt(p.age) <= 21 },
  { label: "Ebony",        emoji: "👑", to: "/category/ebony-cam-girls",           match: p => p.ethnicity?.toLowerCase() === 'ebony' || p.ethnicity?.toLowerCase() === 'black' },
  { label: "Asian",        emoji: "🌸", to: "/category/asian-cam-girls",           match: p => p.ethnicity?.toLowerCase() === 'asian' },
  { label: "Mature 40+",   emoji: "💋", to: "/category/mature-cam-girls",          match: p => parseInt(p.age) >= 40 },
  { label: "Blonde",       emoji: "👱", to: "/category/blonde-cam-girls",          match: p => p.hairColor?.toLowerCase() === 'blond' || p.hairColor?.toLowerCase() === 'blonde' },
  { label: "Brunette",     emoji: "🍫", to: "/category/brunette-cam-girls",        match: p => p.hairColor?.toLowerCase() === 'brown' || p.hairColor?.toLowerCase() === 'brunette' },
  { label: "Feet",         emoji: "🦶", to: "/category/feet-cam-girls",            match: p => (p.fetishes || []).some(f => /feet/i.test(f)) },
  { label: "Roleplay",     emoji: "🎭", to: "/category/roleplay-cam-girls",        match: p => (p.fetishes || []).some(f => /roleplay/i.test(f)) },
  { label: "BDSM",         emoji: "⛓️", to: "/category/bdsm-cam-girls",            match: p => (p.fetishes || []).some(f => /bdsm|bondage|submissive|dominant/i.test(f)) },
  { label: "Dominant",     emoji: "👠", to: "/category/dominant-cam-girls",        match: p => (p.fetishes || []).some(f => /dominant|femdom/i.test(f)) },
  { label: "Interactive",  emoji: "📳", to: "/category/interactivetoy-cam-girls",  match: p => (p.fetishes || []).some(f => /interactivevibe/i.test(f)) },
  { label: "Squirt",       emoji: "💦", to: "/category/squirt-cam-girls",          match: p => (p.fetishes || []).some(f => /squirt/i.test(f)) },
  { label: "Voyeur",       emoji: "👁️", to: "/category/voyeur-cam-girls",          match: p => (p.fetishes || []).some(f => /voyeur/i.test(f)) },
];

// ── Filter config ─────────────────────────────────────────────────────────────
const AGE_OPTIONS     = ["Teen 18+", "20s", "30s", "40s", "Mature 50+"];
const ETHNICITY_OPTS  = ["Asian", "Black", "Caucasian", "Hispanic", "Middle Eastern"];
const HAIR_OPTS       = ["Blonde", "Brunette", "Black", "Red", "Other"];
const AGE_RANGES      = {
  "Teen 18+": [18,19], "20s": [20,29], "30s": [30,39],
  "40s": [40,49], "Mature 50+": [50,100],
};

// ── Pill button ───────────────────────────────────────────────────────────────
const Pill = ({ label, active, onClick }) => (
  <Box onClick={onClick} sx={{
    display: "inline-flex", alignItems: "center",
    px: 1.4, py: 0.35, borderRadius: "20px",
    border: active ? "1.5px solid #FF4196" : "1.5px solid #ddd",
    backgroundColor: active ? "rgba(255,65,150,0.08)" : "#f5f5f5",
    color: active ? "#FF4196" : "#555",
    fontSize: "0.73rem", fontWeight: 600,
    cursor: "pointer", userSelect: "none",
    transition: "all 0.15s",
    "&:hover": { borderColor: "#FF4196", color: "#FF4196" },
  }}>
    {label}
  </Box>
);

const SectionLabel = ({ children }) => (
  <Typography sx={{
    fontSize: "0.68rem", fontWeight: 800, letterSpacing: 1.2,
    color: "#999", textTransform: "uppercase", mb: 1, mt: 2,
  }}>
    {children}
  </Typography>
);

// ── Main component ────────────────────────────────────────────────────────────
const LeftSidebar = () => {
  const { performers: performersMain, handleFilterChange } = useContext(PerformerContext);
  const awContext = useContext(PerformerAWContext);
  const [searchTerm, setSearchTerm] = useState("");
  // Build category thumbnails from already-loaded performers (no extra API calls)
  // Pick the first unique performer per category; fallback to any unmatched performer
  const categories = useMemo(() => {
    const used = new Set();
    const allPerfs = [...(performersMain || [])];
    return CATEGORY_DEFS.map(cat => {
      // Primary: strict category match
      let match = allPerfs.find(p => cat.match(p) && p.profilePictureUrl && !used.has(p.profilePictureUrl));
      // Fallback: any performer with a thumbnail not yet used
      if (!match) match = allPerfs.find(p => p.profilePictureUrl && !used.has(p.profilePictureUrl));
      const img = match?.profilePictureUrl || null;
      if (img) used.add(img);
      return { ...cat, img };
    });
  }, [performersMain]); // eslint-disable-line
  const [activeAge,  setActiveAge]  = useState([]);
  const [activeEth,  setActiveEth]  = useState([]);
  const [activeHair, setActiveHair] = useState([]);

  const applyFilters = ({ age = activeAge, eth = activeEth, hair = activeHair, search = searchTerm } = {}) => {
    const filters = {
      ethnicity:  eth.map(e => e.toLowerCase()),
      age, hairColor: hair.map(h => h.toLowerCase()),
      searchTerm: search,
      ageRanges:  age.map(a => AGE_RANGES[a]).filter(Boolean),
    };
    handleFilterChange(filters);
    awContext?.handleFilterChange?.(filters);
  };

  const togglePill = (val, active, setActive, key) => {
    const next = active.includes(val) ? active.filter(x => x !== val) : [...active, val];
    if (key === "age")  { setActiveAge(next);  applyFilters({ age: next }); }
    if (key === "eth")  { setActiveEth(next);  applyFilters({ eth: next }); }
    if (key === "hair") { setActiveHair(next); applyFilters({ hair: next }); }
  };

  const handleSearch = (e) => {
    const val = e.target.value;
    setSearchTerm(val);
    applyFilters({ search: val });
    if (val.trim().length >= 2) {
      fetch(process.env.REACT_APP_API_URL + "/api/search-log", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ term: val.trim().toLowerCase() }),
      }).catch(() => {});
    }
  };

  const clearAll = () => {
    setActiveAge([]); setActiveEth([]); setActiveHair([]); setSearchTerm("");
    applyFilters({ age: [], eth: [], hair: [], search: "" });
  };

  const hasFilters = activeAge.length || activeEth.length || activeHair.length || searchTerm;

  return (
    <Box sx={{
      width: 260, height: "100vh",
      bgcolor: "#fff",
      borderRight: "1px solid #ececec",
      overflowY: "auto", overflowX: "hidden",
      display: "flex", flexDirection: "column",
      scrollbarWidth: "thin", scrollbarColor: "#FF4196 #f5f5f5",
      "&::-webkit-scrollbar": { width: 4 },
      "&::-webkit-scrollbar-thumb": { background: "#FF4196", borderRadius: 2 },
    }}>

      <Divider sx={{ borderColor: "#f0f0f0", mt: 1 }} />

      {/* ── Top Categories with images ── */}
      <Box sx={{ px: 2, pt: 1.5 }}>
        <SectionLabel>Top Categories</SectionLabel>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0 }}>
          {categories.map(({ label, emoji, to, img }) => (
            <Box
              key={to}
              component={Link}
              to={to}
              sx={{
                display: "flex", alignItems: "center", gap: 1.5,
                px: 1, py: 0.8, borderRadius: "10px",
                textDecoration: "none", color: "#222",
                "&:hover": {
                  bgcolor: "rgba(255,65,150,0.06)",
                  "& .cat-label": { color: "#FF4196" },
                },
                transition: "all 0.15s",
              }}
            >
              {/* Category thumbnail */}
              <Box sx={{
                width: 42, height: 42, borderRadius: "10px",
                overflow: "hidden", flexShrink: 0,
                bgcolor: "#f0f0f0",
                border: "1.5px solid #eee",
              }}>
                <img
                  src={img}
                  alt={label}
                  style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                  onError={e => {
                    // Fallback: show emoji on colored bg
                    e.target.style.display = "none";
                    e.target.parentElement.style.display = "flex";
                    e.target.parentElement.style.alignItems = "center";
                    e.target.parentElement.style.justifyContent = "center";
                    e.target.parentElement.style.fontSize = "1.4rem";
                    e.target.parentElement.style.background = "linear-gradient(135deg,#FF4196,#ff6b6b)";
                    e.target.parentElement.innerHTML = emoji;
                  }}
                />
              </Box>
              {/* Label */}
              <Typography className="cat-label" sx={{
                fontWeight: 700, fontSize: "0.83rem", transition: "color 0.15s",
              }}>
                {emoji} {label}
              </Typography>
              {/* Live dot */}
              <Box sx={{ ml: "auto", width: 7, height: 7, borderRadius: "50%",
                bgcolor: "#4CAF50", boxShadow: "0 0 5px #4CAF50", flexShrink: 0 }} />
            </Box>
          ))}
        </Box>
      </Box>

      <Divider sx={{ borderColor: "#f0f0f0", mt: 1 }} />

      {/* ── Age filter ── */}
      <Box sx={{ px: 2 }}>
        <SectionLabel>Age</SectionLabel>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.6 }}>
          {AGE_OPTIONS.map(opt => (
            <Pill key={opt} label={opt}
              active={activeAge.includes(opt)}
              onClick={() => togglePill(opt, activeAge, setActiveAge, "age")}
            />
          ))}
        </Box>
      </Box>

      {/* ── Ethnicity filter ── */}
      <Box sx={{ px: 2 }}>
        <SectionLabel>Ethnicity</SectionLabel>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.6 }}>
          {ETHNICITY_OPTS.map(opt => (
            <Pill key={opt} label={opt}
              active={activeEth.includes(opt)}
              onClick={() => togglePill(opt, activeEth, setActiveEth, "eth")}
            />
          ))}
        </Box>
      </Box>

      {/* ── Hair color filter ── */}
      <Box sx={{ px: 2, mb: 2 }}>
        <SectionLabel>Hair Color</SectionLabel>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.6 }}>
          {HAIR_OPTS.map(opt => (
            <Pill key={opt} label={opt}
              active={activeHair.includes(opt)}
              onClick={() => togglePill(opt, activeHair, setActiveHair, "hair")}
            />
          ))}
        </Box>
      </Box>

      {/* ── Clear filters ── */}
      {hasFilters ? (
        <Box sx={{ px: 2, mb: 2 }}>
          <Box onClick={clearAll} sx={{
            textAlign: "center", py: 0.6, borderRadius: "8px",
            border: "1px solid #eee", color: "#999", fontSize: "0.75rem",
            cursor: "pointer", "&:hover": { borderColor: "#FF4196", color: "#FF4196" },
          }}>
            ✕ Clear all filters
          </Box>
        </Box>
      ) : null}
    </Box>
  );
};

export default LeftSidebar;
